import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IProduct } from "types/productManagementTypes";
import {
  ConfigKeyOption,
  LogModeOption,
  PLAYER_PREF_MIN_VERSION,
  PlayerPrefChannelKey,
  normalizeConfigKey,
  normalizeLogMode,
  parseDeviceBool,
  parseDeviceBoolWithDefault,
  pendingPlayerPrefValues,
  supportsPlayerPref,
} from "@utils/deviceConfigKeys";
import DevicePlayerPrefsControls, {
  DevicePlayerPrefChange,
  confirmationCopy,
  playerPrefValue,
} from "./DevicePlayerPrefsControls";

interface PlayerPrefsEditorProps {
  onSubmit: (key: string, value: string) => void;
  submitting?: boolean;
  product?: IProduct | null;
}

const PlayerPrefsEditor: React.FC<PlayerPrefsEditorProps> = ({ onSubmit, submitting, product }) => {
  const { t } = useTranslation();
  const reportedPrefs = ((product?.states ?? {}) as Record<string, unknown>).playerPrefs as
    | Record<string, unknown>
    | undefined;
  const queued = pendingPlayerPrefValues(product);
  const playerPrefs: Record<string, unknown> = { ...reportedPrefs, ...queued };

  const [configKey, setConfigKey] = useState<ConfigKeyOption>("unset");
  const [enableLogger, setEnableLogger] = useState(false);
  const [enableFileLogger, setEnableFileLogger] = useState(true);
  const [enableLokiLogger, setEnableLokiLogger] = useState(true);
  const [logMode, setLogMode] = useState<LogModeOption>("default");
  const [disableKeyConfigOnTest, setDisableKeyConfigOnTest] = useState(false);
  const [pending, setPending] = useState<DevicePlayerPrefChange | null>(null);
  const [sending, setSending] = useState<PlayerPrefChannelKey | null>(null);

  useEffect(() => {
    setEnableLogger(parseDeviceBool(playerPrefs?.enableLogger));
    setEnableFileLogger(parseDeviceBoolWithDefault(playerPrefs?.enableFileLogger, true));
    setEnableLokiLogger(parseDeviceBoolWithDefault(playerPrefs?.enableLokiLogger, true));
    setLogMode(normalizeLogMode(playerPrefs?.logMode));
    setConfigKey(normalizeConfigKey(playerPrefs?.configKey));
    setDisableKeyConfigOnTest(parseDeviceBool(playerPrefs?.disableKeyConfigOnTest));
  }, [
    playerPrefs?.enableLogger,
    playerPrefs?.enableFileLogger,
    playerPrefs?.enableLokiLogger,
    playerPrefs?.logMode,
    playerPrefs?.configKey,
    playerPrefs?.disableKeyConfigOnTest,
  ]);

  const supports = (key: PlayerPrefChannelKey) => supportsPlayerPref(key, product?.version);
  const tooOldNote = (label: string, key: PlayerPrefChannelKey) =>
    product ? (
      <Text size="xs" c="dimmed">
        {t("DeviceConfig.versionRequired", {
          label,
          minimum: PLAYER_PREF_MIN_VERSION[key],
          version: product.version || t("DeviceConfig.noVersion"),
        })}
      </Text>
    ) : null;

  const confirmPendingChange = () => {
    if (!pending) return;
    setSending(pending.kind);
    onSubmit(pending.kind, playerPrefValue(pending));
  };

  useEffect(() => {
    if (submitting || !sending) return;
    setSending(null);
    setPending(null);
  }, [submitting, sending]);

  return (
    <>
      <DevicePlayerPrefsControls
        configKey={configKey}
        enableLogger={enableLogger}
        enableFileLogger={enableFileLogger}
        enableLokiLogger={enableLokiLogger}
        logMode={logMode}
        disableKeyConfigOnTest={disableKeyConfigOnTest}
        pendingChange={pending}
        queued={queued}
        working={sending}
        onConfigKeyChange={setConfigKey}
        onRequestChange={setPending}
        supports={supports}
        tooOldNote={tooOldNote}
        showDividers={!!product}
        expectedSerial={product?.states?.serialNo}
      />
      <Modal
        opened={pending !== null}
        onClose={() => (submitting ? null : setPending(null))}
        title={pending ? confirmationCopy(pending, t).title : ""}
        centered
        closeOnClickOutside={!submitting}
        closeOnEscape={!submitting}
        withCloseButton={!submitting}
      >
        {pending && (
          <Stack gap="md">
            <Text size="sm">{confirmationCopy(pending, t).message}</Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setPending(null)} disabled={submitting}>
                {t("cancel")}
              </Button>
              <Button
                color={confirmationCopy(pending, t).color}
                onClick={confirmPendingChange}
                loading={submitting}
              >
                {t("confirm")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default PlayerPrefsEditor;
